import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { PhoneIcon } from 'assets/icons';
import useText from 'hooks/useText';
import { telHref } from 'pages/prospection/callListItems';
import { dialPhone } from 'server/crm/prospectionApi';

// The tel: link hands the number to the system, which gives it to x-bees:
// through a link rather than location.href so that the page stays where it is.
export const dialThroughTel = (phone) => {
  const href = telHref(phone);
  if (!href) return;
  const link = document.createElement('a');
  link.href = href;
  link.click();
};

// The api asked Wildix and got no answer: the call may be ringing, dialing
// again through tel: could make it twice. Read by its code: a 502 without it
// comes from a proxy (the api restarting), and nothing was dialed.
const MAY_BE_RINGING = 'WILDIX_NO_ANSWER';

// One dialing at a time, for the whole app: a second click (or A) while
// Wildix is asked would dial the same number twice.
let dialing = false;

/**
 * Wildix dials `phone` from the caller's own device, the screen staying in
 * Jobbiz (`companyId` goes along, for the agency number to come). Whenever
 * nothing was dialed (Wildix unknown to the api, the caller unknown to
 * Wildix, no device connected, the api unreachable), tel: takes over as
 * before.
 */
export const dial = (phone, { companyId, onRinging, onFailed } = {}) => {
  if (!telHref(phone) || dialing) return Promise.resolve();
  dialing = true;
  return dialPhone({ phone, companyId })
    .then(
      () => onRinging?.(),
      (error) => {
        if (error?.response?.data?.errorCode === MAY_BE_RINGING) onFailed?.();
        else dialThroughTel(phone);
      },
    )
    .finally(() => {
      dialing = false;
    });
};

// dial(phone, companyId), with the toasts that tell the caller to pick up, or
// that Wildix did not confirm the call.
export const useDial = () => {
  const { text } = useText('record');
  return useCallback(
    (phone, companyId) =>
      dial(phone, {
        companyId,
        onRinging: () => toast.info(text('dialRinging')),
        onFailed: () => toast.error(text('dialFailed')),
      }),
    [text],
  );
};

// The people that can be called ([{ name, phone }]), each number once.
export const callNumbers = (people) => {
  const seen = new Set();
  return people.filter((person) => {
    const href = telHref(person?.phone);
    if (!href || seen.has(href)) return false;
    seen.add(href);
    return true;
  });
};

/**
 * « Appel » of a record (QuickActions): one number calls it at once through
 * `dialNumber` (useDial), several open a menu to pick whom to call, none calls
 * `onNone`.
 */
export const callAction = ({ label, people, onNone, dial: dialNumber = dial }) => {
  const numbers = callNumbers(people);
  const action = { key: 'call', label, icon: PhoneIcon };
  if (!numbers.length) return { ...action, onClick: onNone };
  if (numbers.length === 1) return { ...action, onClick: () => dialNumber(numbers[0].phone) };
  return {
    ...action,
    menu: numbers.map((person) => ({ label: `${person.name} · ${person.phone}`, onClick: () => dialNumber(person.phone) })),
  };
};
